import { isIosCompanionApp } from "@/lib/native/runtime";
import type { TourStep } from "@/shared/components/Coachmark";

/**
 * "What is this page for, and what can I do here?" — per screen.
 *
 * DELIBERATELY a data registry keyed by route, not help wired into each page.
 * The same launcher has to work on the dashboard, on every catalogue screen and
 * inside the store editor, and a component that each page has to remember to
 * mount is a component half the pages will not have. One registry, one resolver,
 * one launcher on the shell: adding help to a new screen is a data change.
 *
 * A step with `anchor: null` explains the SCREEN (centred card, no cut-out); a
 * step with an anchor points at one control. Every help tour opens with the
 * former — the seller is told where they are before they are shown a button.
 * Anchors are the same `data-tour` attributes the setup walkthrough uses, and
 * carry the same contract: a missing one is skipped, never fatal.
 */
export type PageHelp = {
  /** Stable id — also the i18n key prefix under `pageHelp.*`. */
  id: string;
  /** Matches the LAST meaningful path segment (after locale + store slug). */
  match: (segment: string, pathname: string) => boolean;
  /** Built from the catalog so copy stays translatable. */
  steps: (t: (key: string) => string) => TourStep[];
};

/** `/ar/<store>/products/123` → `products`; the dashboard resolves to `dashboard`. */
export function pageSegment(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  // [locale, storeSlug, ...rest]
  const rest = parts.slice(2);
  return rest[0] ?? "dashboard";
}

/** One screen-level card plus any anchored steps that exist on the page. */
function help(
  id: string,
  anchors: {
    anchor: string;
    key: string;
    provided?: boolean;
    awaitClick?: boolean;
  }[] = [],
  /**
   * Overrides the default "last segment is the id" match. The store editor
   * lives at `<store>/store/editor`, whose first segment is `store` — the
   * plain match never fired there and the editor silently had no help at all.
   */
  match?: (segment: string, pathname: string) => boolean,
): PageHelp {
  return {
    id,
    match: match ?? ((segment) => segment === id),
    steps: (t) => [
      {
        // The whole content area, so the opening step shows the seller WHICH
        // screen is being described rather than dimming everything flat. Falls
        // back to a centred card if the shell is not around it (the editor).
        anchor: "page-main",
        title: t(`pageHelp.${id}.title`),
        body: t(`pageHelp.${id}.body`),
      },
      ...anchors.map((entry) => ({
        anchor: entry.anchor,
        title: t(`pageHelp.${id}.${entry.key}Title`),
        body: t(`pageHelp.${id}.${entry.key}Body`),
        provided: entry.provided ?? false,
        awaitClick: entry.awaitClick ?? false,
      })),
    ],
  };
}

const REGISTRY: PageHelp[] = [
  help("dashboard", [
    { anchor: "dash-stats", key: "stats" },
    { anchor: "dash-chart", key: "chart" },
    { anchor: "dash-recent", key: "recent" },
  ]),
  help("orders", [
    { anchor: "orders-list", key: "list", awaitClick: true },
    { anchor: "order-detail", key: "detail", provided: true },
  ]),
  help("products", [
    { anchor: "prod-add", key: "add" },
    { anchor: "products-list", key: "list", awaitClick: true },
    { anchor: "product-detail", key: "detail", provided: true },
  ]),
  help("categories", [
    { anchor: "cat-add", key: "add" },
    { anchor: "categories-list", key: "list", awaitClick: true },
    { anchor: "category-form", key: "form", provided: true },
  ]),
  help("customers", [
    // The seller OPENS a customer rather than pressing Next: a hand taps the
    // first row, and their click is what reveals the profile the next step
    // explains. Reading "here is your list" and then pressing a button in the
    // corner teaches the button.
    { anchor: "customers-list", key: "list", awaitClick: true },
    // `provided`: the desktop split always renders the detail pane, but on a
    // phone the profile is a full screen that does not exist until the tap on
    // the step above opens it. Without the flag the open-time anchor filter
    // dropped it and the phone offered two steps where the desktop offered
    // three — the same tour has to explain the same three things on both.
    { anchor: "customer-detail", key: "detail", provided: true },
  ]),
  help("coupons", [
    { anchor: "coupons-add", key: "add" },
    { anchor: "coupons-list", key: "list" },
  ]),
  help("discounts", [
    { anchor: "discounts-add", key: "add" },
    { anchor: "discounts-list", key: "list" },
  ]),
  help("reviews", [{ anchor: "reviews-list", key: "list" }]),
  help("payments", [
    { anchor: "wallet-balance", key: "balance" },
    { anchor: "wallet-topup", key: "topup" },
    { anchor: "payments-history", key: "history" },
  ]),
  help("settings", [{ anchor: "settings-sections", key: "sections" }]),
  help(
    "editor",
    [
      { anchor: "editor-canvas", key: "canvas" },
      { anchor: "editor-publish", key: "publish" },
    ],
    (_segment, pathname) => /\/store\/editor(\/|$)/.test(pathname),
  ),
];

export function resolvePageHelp(pathname: string): PageHelp | null {
  const segment = pageSegment(pathname);
  const entry = REGISTRY.find((entry) => entry.match(segment, pathname)) ?? null;
  if (entry?.id === "payments" && isIosCompanionApp()) return {
    ...entry,
    steps: (t) => [{
      anchor: "page-main",
      title: t("payments.pageTitle"),
      body: t("iosCompanion.paymentsHelp"),
    }],
  };
  return entry;
}

/**
 * The steps a screen can actually show, resolved against the DOM in front of
 * the seller when the tour OPENS.
 *
 * A page's regions differ by breakpoint — the customers list and profile pane
 * are the desktop split view; the phone renders one full-bleed screen — and by
 * what the store has in it. A fixed list promised "step 1 of 3" and then jumped
 * to the end.
 *
 * Pure, and takes its own presence test, so the rules below are testable
 * without a DOM and without a store in any particular state.
 */
export function visibleSteps(
  all: readonly TourStep[],
  isPresent: (anchor: string) => boolean,
): TourStep[] {
  // Walked in order: whether a step survives can depend on the one before it.
  let previousKept = true;
  return all.filter((step, i) => {
    const survives =
      // The OPENING step always survives. It answers "what is this screen?",
      // which is the one question every tour must answer, and it anchors
      // `page-main` — the shell's content region, which the full-screen editor
      // does not have. Filtered like the rest, the editor's tour lost its first
      // step and opened mid-explanation. A missing rect is not a missing step:
      // Coachmark presents it screen-level (plain dim, centred card).
      i === 0 ||
      step.anchor === null ||
      (step.provided === true
        ? // `provided` means "the step before me creates this" — the tap on an
          // `awaitClick` step. So it survives only if that step did. On a store
          // with no orders the list step is filtered out for having no list,
          // and the detail step used to sail through on `provided` alone: a
          // card explaining "inside an order" over an empty page, for the
          // second and a half it took to give up and close itself.
          previousKept
        : isPresent(step.anchor));
    previousKept = survives;
    return survives;
  });
}
